import { useEffect, useMemo, useRef, useState } from 'react'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'
import { fetchTmdbByGenre, type TmdbSimilarTitle } from '../tmdb/tmdb'
import { HomeTmdbPosterCard } from './HomePosterCard'
import { LoadMoreCard } from './LoadMoreCard'
import { CarouselNav } from './CarouselNav'

/**
 * Single smart carousel that lets the user pick a genre via a chip
 * row above it. Mirrors desktop's `TOP_RATED_BY_GENRE` carousel + the
 * `GenreSelector` dropdown — one row, many genres.
 *
 * Pagination: each genre has its own entry in the cache holding
 * `{ items, nextPage, totalPages }`. First visit fetches page 1 of
 * /discover/movie. Clicking "Load more" either reveals more from the
 * cache (if we still have unseen items in memory) or fetches the
 * next TMDB page and appends it. The button stays visible as long as
 * there are more items on TMDB or more cached items to reveal.
 *
 * "All Genres" uses a higher vote-count floor (5000) than individual
 * genres (1000) — without `with_genres` the pool is huge and we want
 * only the obvious classics.
 */

interface GenreOption {
  id: number
  label: string
}

// Same set as desktop's `GenreSelector.tsx` so the PWA's chip row
// matches the desktop dropdown 1:1.
const GENRES: GenreOption[] = [
  { id: 0, label: 'All Genres' },
  { id: 28, label: 'Action' },
  { id: 12, label: 'Adventure' },
  { id: 16, label: 'Animation' },
  { id: 35, label: 'Comedy' },
  { id: 80, label: 'Crime' },
  { id: 99, label: 'Documentary' },
  { id: 18, label: 'Drama' },
  { id: 10751, label: 'Family' },
  { id: 14, label: 'Fantasy' },
  { id: 36, label: 'History' },
  { id: 27, label: 'Horror' },
  { id: 10402, label: 'Music' },
  { id: 9648, label: 'Mystery' },
  { id: 10749, label: 'Romance' },
  { id: 878, label: 'Sci-Fi' },
  { id: 53, label: 'Thriller' },
  { id: 10752, label: 'War' },
  { id: 37, label: 'Western' },
]

interface Props {
  selectedGenreId: number
  onGenreChange: (id: number) => void
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
  userMovieRatings: Map<number, number>
  /**
   * TMDB ids already in the user's library — filtered out of the
   * row so the user only sees titles they haven't touched yet.
   */
  libraryMovieIds: Set<number>
}

interface GenreCacheEntry {
  items: TmdbSimilarTitle[]
  /** Next TMDB page number to fetch (1-indexed). */
  nextPage: number
  /** Total pages TMDB reports for this query. 0 means "unknown". */
  totalPages: number
}

const PAGE_SIZE = 20

function voteCountFloorFor(genreId: number): number {
  // All Genres — the unfiltered top-rated pool is massive, so we
  // tighten the floor to keep obvious classics only.
  return genreId === 0 ? 5000 : 1000
}

export function GenreRotatingRow({
  selectedGenreId,
  onGenreChange,
  onOpenDetail,
  userMovieRatings,
  libraryMovieIds,
}: Props) {
  const { settings } = useUserSettings()
  const [cache, setCache] = useState<Map<number, GenreCacheEntry>>(() => new Map())
  const [loadingInitial, setLoadingInitial] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [visible, setVisible] = useState(PAGE_SIZE)
  const scrollRef = useRef<HTMLDivElement>(null)
  // Guards against a double-fire when the user clicks the Load More
  // tile twice in a row while a fetch is still in flight.
  const fetchInFlightRef = useRef(false)
  // Falls back to the bundled developer key when the user hasn't supplied one.
  const apiKey = getEffectiveTmdbApiKey(settings)
  const language = settings?.tmdbPreferredLanguage || 'en'

  // Reset pagination + scroll when the genre flips so each genre
  // starts with its first page visible at the leftmost tile.
  useEffect(() => {
    setVisible(PAGE_SIZE)
    scrollRef.current?.scrollTo({ left: 0 })
  }, [selectedGenreId])

  // Fetch page 1 when the user picks a genre that hasn't been
  // cached yet. Subsequent pages are lazy-loaded via the Load More
  // button.
  useEffect(() => {
    if (!apiKey) return
    if (cache.has(selectedGenreId)) return
    let cancelled = false
    setLoadingInitial(true)
    fetchTmdbByGenre(
      selectedGenreId,
      apiKey,
      language,
      voteCountFloorFor(selectedGenreId),
      1
    ).then(({ items, totalPages }) => {
      if (cancelled) return
      setCache((prev) => {
        const next = new Map(prev)
        next.set(selectedGenreId, { items, nextPage: 2, totalPages })
        return next
      })
      setLoadingInitial(false)
    })
    return () => {
      cancelled = true
    }
  }, [apiKey, language, selectedGenreId, cache])

  const entry = cache.get(selectedGenreId) ?? null
  const rawItems = entry?.items ?? []
  const unseenItems = useMemo(
    () => rawItems.filter((t) => !libraryMovieIds.has(t.tmdbId)),
    [rawItems, libraryMovieIds]
  )
  const items = useMemo(() => unseenItems.slice(0, visible), [unseenItems, visible])
  const selectedLabel =
    GENRES.find((g) => g.id === selectedGenreId)?.label ?? 'All Genres'

  const hasMorePages = !!entry && entry.nextPage <= entry.totalPages
  const hasRevealable = visible < unseenItems.length
  const canLoadMore = hasRevealable || hasMorePages

  const handleLoadMore = async () => {
    if (fetchInFlightRef.current) return

    // Fast path: more cached-unseen items than currently visible —
    // just bump the slice.
    if (hasRevealable) {
      setVisible((v) => v + PAGE_SIZE)
      return
    }

    // Slow path: we've run out of cached items; go fetch TMDB's
    // next page and append it.
    if (!apiKey || !entry || entry.nextPage > entry.totalPages) return
    fetchInFlightRef.current = true
    setLoadingMore(true)
    try {
      const { items: newItems, totalPages } = await fetchTmdbByGenre(
        selectedGenreId,
        apiKey,
        language,
        voteCountFloorFor(selectedGenreId),
        entry.nextPage
      )
      setCache((prev) => {
        const next = new Map(prev)
        const existing = next.get(selectedGenreId)
        if (!existing) return prev
        next.set(selectedGenreId, {
          items: [...existing.items, ...newItems],
          nextPage: existing.nextPage + 1,
          totalPages: totalPages || existing.totalPages,
        })
        return next
      })
      setVisible((v) => v + PAGE_SIZE)
    } finally {
      setLoadingMore(false)
      fetchInFlightRef.current = false
    }
  }

  return (
    <section className="relative" aria-label={`Browse by genre — ${selectedLabel}`}>
      <header className="mb-2 flex flex-wrap items-baseline justify-between gap-2 px-4">
        <div>
          <h2 className="font-inter text-base font-semibold text-text-primary sm:text-lg">
            Browse by Genre
          </h2>
          <p className="text-[11px] text-text-tertiary sm:text-xs">
            Top rated in {selectedLabel} — titles you haven't seen yet
          </p>
        </div>
      </header>

      <div className="mb-3 flex gap-2 overflow-x-auto px-4 pb-1 scrollbar-hide">
        {GENRES.map((g) => {
          const active = g.id === selectedGenreId
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => onGenreChange(g.id)}
              className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                active
                  ? 'border-accent-primary bg-accent-primary/10 text-accent-primary'
                  : 'border-border-primary bg-bg-secondary text-text-secondary hover:border-accent-primary/40 hover:text-text-primary'
              }`}
              aria-pressed={active}
            >
              {g.label}
            </button>
          )
        })}
      </div>

      <div className="relative">
        <div
          ref={scrollRef}
          role="list"
          className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-4 scrollbar-hide sm:gap-4"
          aria-busy={loadingInitial || loadingMore || undefined}
        >
          {items.length > 0 ? (
            <>
              {items.map((it) => (
                <HomeTmdbPosterCard
                  key={`gr-${selectedGenreId}-${it.tmdbId}`}
                  item={it}
                  userRating={userMovieRatings.get(it.tmdbId) ?? null}
                  onClick={() => onOpenDetail(it.tmdbId, 'movie')}
                />
              ))}
              {canLoadMore && (
                <LoadMoreCard
                  onClick={handleLoadMore}
                  label={loadingMore ? 'Loading…' : 'Load more'}
                  hint={
                    hasRevealable
                      ? `+${Math.min(PAGE_SIZE, unseenItems.length - visible)} more`
                      : `+${PAGE_SIZE} more`
                  }
                />
              )}
            </>
          ) : loadingInitial ? (
            <GenreSkeletons />
          ) : (
            <div className="w-full py-8 text-center text-xs text-text-tertiary">
              No titles found for this genre.
            </div>
          )}
        </div>
        {items.length > 0 && (
          <CarouselNav
            scrollRef={scrollRef}
            ariaLabel={`Browse by genre ${selectedLabel}`}
          />
        )}
      </div>
    </section>
  )
}

function GenreSkeletons() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="shrink-0 snap-start"
          style={{ width: 'clamp(120px, 16vw, 180px)' }}
        >
          <div className="relative aspect-[2/3] animate-pulse overflow-hidden rounded-lg border border-border-primary bg-bg-tertiary">
            <div className="absolute inset-0 bg-gradient-to-r from-bg-tertiary via-bg-secondary to-bg-tertiary" />
          </div>
          <div className="mt-2 space-y-1">
            <div className="h-3 animate-pulse rounded bg-bg-tertiary" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-bg-tertiary" />
          </div>
        </div>
      ))}
    </>
  )
}
